// In your Redux Slice
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import React from 'react';
import { useField } from 'formik';
import axios from 'axios';
import _ from 'lodash';
import { allRoutes } from '../constants/config'

import { Checkbox, Dropdown, Input } from 'semantic-ui-react';
import { fetchSignedUrlByType, fetchSignedUrlFromAudioBookDataPathUtil } from '../utils/fetchSignedUrlByFolderV2';

const {
  // DELETE_TYPES,
  ENTITY_ROLES
} = require('../constants/config');

const {
  BASE_URL, ADD_API_STORY_BOOK_META_DATA, GET_FULL_BOOK_BY_USER, GET_ALL_BOOKS_BY_USER,
  UPLOAD_API_BOOK_SOURCE,
  PROCESS_API_PDF_BOOK_SOURCE,
  AAI_API_MOVE_FILE
} = { ...allRoutes }
// root keys:
// HAS_AUTHOR, HAS_CHARACTERS, HAS_BOOK_SAMPLES, HAS_BOOK_SOURCE, HAS_BOOK_STATISTICS, HAS_PROCESSED_ITEMS

export const getBookTreeElement = createAsyncThunk(
  'books/getBookTreeElement',
  async (key, { getState }) => {
    const state = getState();

    if (!state?.user?.storyText?.activeFullBookTreeData) {
      return null;
    }

    const bookTree = state.user.storyText.activeFullBookTreeData.bookTree;
    const userStatistics = state.user.storyText.activeFullBookTreeData.userStatistics;

    if (!bookTree || !userStatistics) {
      return null;
    }

    switch (key) {
      case 'HAS_AUTHOR':
        return bookTree[key]?.[0] || null;
      case 'HAS_CHARACTERS':
      case 'HAS_BOOK_SAMPLES':
      case 'HAS_BOOK_SOURCE':
      case 'HAS_BOOK_STATISTICS':
        return bookTree[key]?.[0] || { totalProcTime: 0, promptTokens: 0, totalTokens: 0, completionTokens: 0 };
      case 'HAS_PROCESSED_ITEMS':
        return bookTree[key] || null;
      case 'HAS_CHARACTER':
        return bookTree['HAS_CHARACTERS']?.[0]?.HAS_CHARACTER || null;
      case 'HAS_PROCESSED_VERSION':
        return bookTree['HAS_PROCESSED_ITEMS']?.[0]?.HAS_PROCESSED_VERSION || null;
      case 'HAS_BOOK':
        return bookTree || null;
      case 'HAS_USER_STATISTICS':
        return userStatistics || {
          dateTime: "2024-06-22T20:52:33.149Z",
          totalProcTime: 0,
          promptTokens: 0,
          mostRecentProcTime: 0,
          uuid: bookTree.uuid, //"123e4567-e89b-12d3-a456-426614174008",
          totalTokens: 0,
          completionTokens: 0
        };
      default:
        return bookTree[key] || null;
    }
  }
);

export const fetchFullBookDataSet = createAsyncThunk(
  'books/fetchFullBookDataSet',
  async (props, { dispatch }) => {
    // Fetch the full book by user
    const response = await axios.get(GET_FULL_BOOK_BY_USER, { params: { ...props } });

    // Phase 4a / Blocker 1 fix (PHASE_4A_FOLLOW_UP_2026_04_18.md §3, §6):
    //
    // The server now ships `processedVersionItems` as a top-level key on the
    // `/api/get-book-full-data` response (see bookController.getBookFullData).
    // Prefer that authoritative, tree-walk-independent array. Fall back to
    // the legacy apoc.convert.toTree walk via getBookTreeElement only if the
    // server payload doesn't include it (older server, or unexpectedly empty
    // collection) so this thunk remains backward compatible.
    //
    // Note: the original `await dispatch(getBookTreeElement(...))` call path
    // read `state.user.storyText.activeFullBookTreeData`, which is set by the
    // fetchFullBookDataSet.fulfilled reducer — i.e. *after* this thunk body
    // returns. That meant the walker ran against stale / null state on first
    // load and was a contributor to the empty-`processedVersionItems` bug.
    // Walking the freshly-fetched `response.data.bookTree` here is both more
    // reliable and scope-limited to this thunk.
    const serverProcessedVersionItems = Array.isArray(response.data?.processedVersionItems)
      ? response.data.processedVersionItems
      : null;

    const walkTreeForProcessedVersions = (tree) => {
      if (!tree) return [];
      const items = tree?.HAS_PROCESSED_ITEMS?.[0]?.HAS_PROCESSED_VERSION;
      return Array.isArray(items) ? items : [];
    };

    const processedVersionItems =
      serverProcessedVersionItems && serverProcessedVersionItems.length > 0
        ? serverProcessedVersionItems
        : walkTreeForProcessedVersions(response.data?.bookTree);

    // `bookTree` is kept as the HAS_BOOK root for backward compatibility with
    // downstream consumers that still read fullBookDataSet.bookTree.
    const bookTree = response.data?.bookTree?.HAS_BOOK?.[0] || response.data?.bookTree || null;

    // Prepare the full book data set
    let fullBookDataSet = {
      activeFullBookTreeData: response.data,
      processedVersionItems: processedVersionItems,
      bookTree: bookTree,
    };

    return fullBookDataSet;
  }
);

export const fetchFullBook = createAsyncThunk('books/fetchFullBook', async (props) => {
  const response = await axios.get(GET_FULL_BOOK_BY_USER, { params: { ...props } });
  return response.data;
}
);

export const fetchAllBooks = createAsyncThunk('books/fetchAllBooks', async (props) => {
  const response = await axios.get(GET_ALL_BOOKS_BY_USER, { params: { ...props } });
  return response.data;
}
);

export const fetchProcessedBookContent = createAsyncThunk(
  'books/fetchProcessedBookContent',
  async ({ bookUuid, contentUrl }, { getState, dispatch }) => {
    const response = await fetch(contentUrl);
    const content = await response.text();

    return { bookUuid, content };
  }
);

export const fetchparseDataFromS3 = createAsyncThunk(
  'books/fetchparseDataFromS3',
  async ({ userId, targetUrl }, { rejectWithValue }) => {
    if (!targetUrl) return rejectWithValue('Target URL is undefined');

    try {
      const s3TextUrl = await fetchSignedUrlFromAudioBookDataPathUtil(userId, targetUrl);
      if (!s3TextUrl) return rejectWithValue('Failed to get signed URL');

      const response = await axios.get(s3TextUrl);
      let returnData = { ...response.data }
      returnData.dnObj.dialogueArray = returnData.dnObj.dialogueArray.map((dialItem, index) => {
        return { ...dialItem, key: index.toString() }
      })
      returnData.dnObj.bookStats = {
        title: "The Luck of Madonna 13",
        dialogCnt: returnData?.dnObj.dialogueArray?.length,
        characterCnt: returnData?.dnObj.charAndEmoLists?.characters?.length,
        totalVariations: returnData?.dnObj.charAndEmoLists?.emotions?.length,
      }
      return returnData;
    } catch (error) {
      // Handle 404 error specifically
      if (error.response && error.response.status === 404) {
        console.warn(`Book JSON file not found: ${targetUrl}. Using fallback data.`);

        // Return a default structure that matches what customToolbar.jsx expects
        // Using 'text' property which is what the component expects
        return {
          dnObj: {
            dialogueArray: [{
              key: "0",
              type: "NOT_FOUND",
              text: "Book data file not found. The book may need to be processed.",
              emotion: "neutral",
              uniqueKey: "uniqueKey_0"
            }],
            bookStats: {
              title: targetUrl ? targetUrl.split('/').pop().replace('.json', '') : 'Unknown Book',
              dialogCnt: 0,
              characterCnt: 0,
              totalVariations: 0,
              error: 'File not found - book may need processing'
            },
            charAndEmoLists: {
              characters: [],
              emotions: []
            }
          },
          needsProcessing: true,
          fileNotFound: true,
          originalUrl: targetUrl
        };
      }

      console.error('Failed to fetch parseData from S3:', error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

// let saveBookParams = {
//   userId: userProfile.data.userId,
//   bookUuid: storySelectedObj.current.data.uuid ? storySelectedObj.current.data.uuid : uuidv4(),
//   fileName: formValues.storyFileName.name.split('.').slice(0, -1).join('.'),
//   thumbnail: formValues.thumbnail.name,
//   processStatistics: processStatistics
// }
// let processStatistics = {
//   ProcessingTime: uploadData.processingTime,
//   UserStatistics: uploadData.UserStatistics,
//   BookStatistics: uploadData.BookStatistics,
//   TokenAccumulator: uploadData.TokenAccumulator
// }
export const addStoryBookItem = createAsyncThunk(
  'books/addStoryBookItem',
  async ({ userId, bookUuid, fileName, thumbnail, processTime, prompt_tokens, completion_tokens, total_tokens }) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      const instance = axios.create({
        baseURL: BASE_URL,  // specify your base URL
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // const response = await instance.post(`${ADD_API_STORY_BOOK_META_DATA}/${userId}/${bookUuid}/${fileName}/${variation}`, { fileName, variation, s3TextUrl, version });

      try {
        const response = await instance.post(`${ADD_API_STORY_BOOK_META_DATA}/${userId}/${bookUuid}/${fileName}/${thumbnail}/${processTime}/${prompt_tokens}/${completion_tokens}/${total_tokens}`);
        return response.data;
      } catch (error) {
        console.error('Error saving text snippet:', error.response.data);
      }
    }
    else {
      console.error('Error saving text snippet: Invalid user token');
    }
  }
);

export const fetchUserAndBookStatistics = createAsyncThunk(
  'statistics/fetchUserAndBookStatistics',
  async (bookUuid, { getState }) => {
    const state = getState();
    let book = null;
    let bookStats = { userStatistics: { totalProcTime: 0 }, bookStatistics: { mostRecentProcTime: 0, totalProcTime: 0, uuid: "" } };
    if (bookUuid && state.user && state.user.storyText.books && state.user.storyText.books.books) {
      book = _.forEach(state.user.storyText.books.books, (book, index) => {
        if (book.book.uuid === bookUuid) {
          bookStats.userStatistics = state.user.storyText.books.books[index].userStatistics;
          bookStats.bookStatistics = state.user.storyText.books.books[index].bookStatistics;
          return book;
        }
      });
      bookStats = book ? book.statistics : null;
    }

    return book ? { book, bookStats } : { book: null, bookStats: {} };
  }
);

export const uploadHtmlToS3 = async (signedUrl, htmlString) => {
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: new Blob([htmlString], { type: 'text/html' }),
      headers: {
        'Content-Type': 'text/html',
      },
    });
    if (!response.ok) {
      throw new Error('Upload failed: ' + response.statusText);
    }
    console.log('Upload succeeded!');
  } catch (error) {
    console.error('Error:', error);
  }
};

export const getBookFolder = (category, categoryOptions) => {
  if (!category || _.isEmpty(category) || (_.isArray(category) && category.length < 1)) return 'unknown';

  let folderName = '';
  if (_.isNumber(category[0])) {
    folderName = categoryOptions[Number(category[0])].text.toLowerCase();
  } else {
    let catText = category[0].toUpperCase();
    let index = _.findIndex(categoryOptions, (x) => x.text.toUpperCase() === catText);
    folderName = categoryOptions[index].text.toLowerCase();
  }

  folderName = folderName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
  return folderName || 'unknown';
};

export const extractUrls = (content) => {
  const regex = /<img src="https:\/\/aai-api\.s3\.us-west-1\.amazonaws\.com\/aai-user\/temp[^"]+"/g;
  return content.match(regex);
}

export const moveAllFiles = async (userId, storyUuid, formContent, content) => {
  const urls = extractUrls(content);
  formContent.current = content;

  if (urls) {
    // map urls to an array of promises
    const promises = urls.map(async url => {
      const oldKey = url.slice('<img src="https://aai-api.s3.us-west-1.amazonaws.com/'.length, -1); // remove the leading part and trailing quotation mark
      const fileName = oldKey.split("/").pop(); // get the filename from the oldKey

      // const categorySubFolderName = getBookFolder(); // this must be set before submitting - or submitting must be inhibited (which it should be because the button only goes live with valid fields)
      let adjustedFileName = fileName.replaceAll('%20', "-")
      const newKey = `aai-users/${userId}/audio-books/${storyUuid}/book-elements/images/${adjustedFileName}`; // construct the new key with the desired location and filename

      // make a GET request to your server endpoint
      const response = await fetch(`${AAI_API_MOVE_FILE}${encodeURIComponent(oldKey)}/${encodeURIComponent(newKey)}`);

      if (response.ok) {
        const jsonResponse = await response.json();
        const newUrl = jsonResponse.newUrl;

        // replace the old url with the new url in formik.values.content
        formContent.current = formContent.current.replace(url, `<img src="${newUrl}"`)
      } else {
        console.error('Error moving file:', response.statusText);
      }
    });

    // await all promises to be resolved
    await Promise.all(promises);
  }
}

export async function uploadFileToS3(signedUrl, file) {
  await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: file
  });
}

export async function uploadFileToServer(file, userId, bookUuid) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${UPLOAD_API_BOOK_SOURCE}/${userId}/${bookUuid}`;
  const response = await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
}

/**
 * Processes an uploaded PDF by sending its details to the API.
 *
 * @param {string} pdfFileName - The name of the PDF file (e.g., 'pdfFile.pdf').
 * @param {string} userId - The user's unique identifier.
 * @param {string} bookUuid - The book's unique UUID.
 * @returns {Promise<Object>} - The response data from the API.
 */
export async function processUploadedPdf(pdfFileName, userId, bookUuid) {
  // Validate inputs
  if (!pdfFileName || !userId || !bookUuid) {
    throw new Error('All parameters (pdfFileName, userId, bookUuid) are required.');
  }

  const url = `${PROCESS_API_PDF_BOOK_SOURCE}/${userId}/${bookUuid}/${pdfFileName}`;

  // Construct the JSON payload
  const payload = {
    pdfFileName,
    userId,
    bookUuid,
  };

  try {
    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    // Enhanced error logging
    console.error('Error processing uploaded PDF:', {
      message: error.message,
      response: error.response ? {
        status: error.response.status,
        data: error.response.data,
      } : null,
      stack: error.stack,
    });
    throw error;
  }
}

export async function uploadPdfFileToS3(pdfFile, userId, bookUuid) {
  const selectedFile = pdfFile;
  if (selectedFile && getFileExt(selectedFile.name).toLowerCase() === 'pdf') {
    // const url = URL.createObjectURL(selectedFile);
    // // setFileUrl(url);

    try {
      const sourcePdfSignedUrl = await fetchSignedUrlByType({
        requestType: 'BOOK_ELEMENTS_SOURCE_PDF',
        userId: userId,
        bookUuid: bookUuid,
        fileName: selectedFile.name
      });

      await uploadFileToS3(sourcePdfSignedUrl, selectedFile);
      console.log('File uploaded successfully');
      return selectedFile.name;
    } catch (error) {
      console.error('Error during file processing:', error);
      return false;
    }
  } else {
    console.log('Please select a PDF file.');
    return false;
  }
};

const getFileExt = (fileName) => {
  return fileName.split('.').pop();
};


export const CustomInputField = ({ ...props }) => {
  const [field, , helpers] = useField(props);

  return (
    <Input
      {...field}
      {...props}
      onChange={(e) => {
        helpers.setValue(e.target.value);
        helpers.setTouched(true, false);
      }}
    />
  );
};

export const CustomCheckBox = ({ field, form, ...props }) => {
  return (
    <Checkbox
      className='ble-checkbox'
      checked={field.value}
      onChange={(e, { checked }) => {
        console.log('CustomCheckBox', field.name, 'checked', checked, 'form', form)
        form.setFieldValue(field.name, checked);
      }
      }
      {...props}
    />
  );
};
export const CustomDropdown = ({ field, form, options, onSelectedBook, ...props }) => (
  <Dropdown
    {...props}
    value={field.value}
    onChange={(e, { value }) => {
      form.setFieldValue(field.name, value)
      if (onSelectedBook !== undefined) {
        let foundStoryOption = _.find(options, (x) => x.value === value);
        console.log('foundStoryOption', foundStoryOption)
        onSelectedBook(foundStoryOption);
      }
    }}
    options={options}
  />
);

export const correctMissingUniqueKeys = (data) => {
  let missingCount = 0;

  return data.map((item) => {
    if (!item.hasOwnProperty('uniqueKey') || !item.uniqueKey) {
      missingCount += 1;
      return {
        ...item,
        uniqueKey: `uniqueKey_${missingCount}`,
      };
    }
    return item;
  });
}
export const excludedEmotions = ['CHAPTER', 'TEXT_BLOCK'];
export const initialData = {
  dnObj: {
    dialogueArray: [{
      key: "key_initial",
      type: "Initialize",
      content: "\"Seriously, you must select a story, and then a version choice to proceed.\""
    }],
    bookStats: {
      title: "The Luck of Madonna 13",
      dialogCnt: 0,
      characterCnt: 0,
      totalVariations: 0
    }

  },
  // Add more initial data if needed
};
export const viewOptions = [
  { key: 'editor', text: 'List', value: 'editor' },
  { key: 'reader', text: 'Notes', value: 'reader' },
];
export const searchOptions = [
  { key: 'all', text: 'All', value: 'all' },
  { key: 'speaker', text: 'Actor', value: 'speaker' },
  // { key: 'uniqueKey', text: 'UniqueKey', value: 'uniqueKey' },
  { key: 'emotion', text: 'Emotion', value: 'emotion' },
  { key: 'content', text: 'Content', value: 'content' },
];

export const countUniqueValues = (data) => {
  const keyCounts = {};
  const emotionCounts = {};
  const speakerCounts = {};

  data.forEach(item => {
    // Count unique 'key' values
    if (item.type !== 'FRONT_MATTER') {
      if (item.type in keyCounts) {
        keyCounts[item.type] += 1;
      } else {
        keyCounts[item.type] = 1;
      }

      // Count unique speakers (actual characters), ignoring rows that have no speaker
      // (e.g. narration / book part / chapter / initialize rows).
      if (typeof item.speaker === 'string' && item.speaker.trim() !== '') {
        const sp = item.speaker.trim();
        speakerCounts[sp] = (speakerCounts[sp] || 0) + 1;
      }

      // Strip the comma-delimited number from the 'emotion' field and count unique emotions
      if (item.emotion !== undefined) {
        if (item.emotion in emotionCounts) {
          emotionCounts[item.emotion] += 1;
        } else {
          emotionCounts[item.emotion] = 1;
        }
      }
      // const strippedEmotion = item.emotion.split(',')[0].trim();
      // if (!excludedEmotions.includes(strippedEmotion)) {
      //   if (strippedEmotion in emotionCounts) {
      //     emotionCounts[strippedEmotion] += 1;
      //   } else {
      //     emotionCounts[strippedEmotion] = 1;
      //   }
      // }
    }
  });

  // Convert keyCounts, emotionCounts, and speakerCounts to arrays
  const keysArray = Object.keys(keyCounts)
    .sort()
    .map(type => ({ type, count: keyCounts[type] }));
  const emotionsArray = Object.keys(emotionCounts)
    .sort()
    .map(emotion => ({ emotion, count: emotionCounts[emotion] }));
  const speakersArray = Object.keys(speakerCounts)
    .sort()
    .map(speaker => ({ speaker, count: speakerCounts[speaker] }));

  return {
    types: keysArray,
    emotions: emotionsArray,
    speakers: speakersArray,
  };
};

const storyEditorSlice = createSlice({
  name: 'storyText',
  initialState: {
    activeFullBookTreeData: null,
    activeStatistics: { book: null, bookStats: {} },
    fetchAllBooksStatus: 'idle',
    fetchFullBookError: null,
    fetchFullBookStatus: 'idle',
    fetchFullBookDataSetStatus: 'idle',
    fetchparseDataFromS3Status: 'idle',
    fetchparseDataFromS3Error: null,
    fullBookDataSet: { activeFullBookTreeData: {}, processedVersionItems: [] },
    editedStoryJson: { activeStoryJson: [], uuid: '', versionNo: -1 },
    isEnabled: false, // Initial value for the enabled state
    parsedStoryData: {
      dnObj: {
        dialogueArray: [{
          key: "key_initial",
          type: "Initialize",
          content: "\"Seriously, you must select a story, and then a version choice to proceed.\""
        }],
        bookStats: {
          title: "The Luck of Madonna 13",
          dialogCnt: 0,
          characterCnt: 0,
          totalVariations: 0
        }
      },
      // Add more initial data if needed
    },
    title: '', // Initial value for the title
    clearStoryProcess: 0,
  },
  reducers: {
    clearStory: (state) => {
      state.clearStoryProcess = state.clearStoryProcess + 1;
      if (state.activeFullBookTreeData) {
        state.activeFullBookTreeData = null;
      }
    },
    setEditedStoryJson: (state, action) => {
      state.editedStoryJson = {
        ...action.payload,
        versionNo: state.editedStoryJson.versionNo + 1
      };
    },
    setStoryText: (state, action) => action.payload,
    setTitle(state, action) {
      // Update the title in the state
      state.title = action.payload;
    },
    toggleEnabled(state) {
      // Toggle the enabled state
      state.isEnabled = !state.isEnabled;
    },
    resetParsedStoryData(state) {
      state.parsedStoryData = {
        dnObj: {
          dialogueArray: [{
            key: "Loading Process",
            uniqueKey: "loading",
            emotion: "loading action",
            text: "\"Loading will typically require several seconds...\"",
          }],
          bookStats: {
            title: "The Luck of Madonna 13",
            dialogCnt: 0,
            characterCnt: 0,
            totalVariations: 0
          }
        }
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllBooks.pending, (state) => {
        state.fetchAllBooksStatus = 'loading';
      })
      .addCase(fetchAllBooks.fulfilled, (state, action) => {

        state.fetchAllBooksStatus = 'succeeded';
        state.books = action.payload;
        console.log('Completed fetchAllBooks', { ...state.books })

      })
      .addCase(fetchAllBooks.rejected, (state, action) => {
        state.fetchAllBooksStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchFullBook.pending, (state) => {
        state.fetchFullBookStatus = 'loading';
      })
      .addCase(fetchFullBook.fulfilled, (state, action) => {
        state.fetchFullBookStatus = 'succeeded';
        state.activeFullBookTreeData = action.payload;
      })
      .addCase(fetchFullBook.rejected, (state, action) => {
        state.fetchFullBookStatus = 'failed';
        state.fetchFullBookError = action.payload;
      })
      .addCase(fetchFullBookDataSet.pending, (state) => {
        state.fetchFullBookDataSetStatus = 'loading';
      })
      .addCase(fetchFullBookDataSet.fulfilled, (state, action) => {
        state.fetchFullBookDataSetStatus = 'succeeded';
        state.fullBookDataSet = action.payload; // Correctly access the payload
        state.activeFullBookTreeData = state.fullBookDataSet.activeFullBookTreeData;
      })
      .addCase(fetchFullBookDataSet.rejected, (state, action) => {
        state.fetchFullBookDataSetStatus = 'failed';
        state.fetchFullBookError = action.payload || action.error.message; // Handle the error properly
      })
      .addCase(fetchProcessedBookContent.fulfilled, (state, action) => {
        if (action.payload.bookUuid) {
          const { bookUuid, content } = action.payload;
          const book = state.books.books.find(book => book.uuid === bookUuid);
          if (book) {
            book.content = content;
          }
        }
      })
      .addCase(fetchparseDataFromS3.pending, (state) => {
        state.fetchparseDataFromS3Status = 'loading';
      })
      .addCase(fetchparseDataFromS3.fulfilled, (state, action) => {
        state.fetchparseDataFromS3Status = 'succeeded';
        state.parsedStoryData = action.payload;
      })
      .addCase(fetchparseDataFromS3.rejected, (state, action) => {
        state.fetchparseDataFromS3Status = 'failed';
        state.fetchparseDataFromS3Error = action.payload;
      })
      .addCase(fetchUserAndBookStatistics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserAndBookStatistics.fulfilled, (state, action) => {
        state.status = 'succeeded';
        console.log('fetchUserAndBookStatistics succeeded', action.payload)
        state.activeStatistics = action.payload;
      })
      .addCase(fetchUserAndBookStatistics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
})

export const { resetParsedStoryData, setEditedStoryJson, setStoryText, setTitle, toggleEnabled, clearStory } = storyEditorSlice.actions
export default storyEditorSlice.reducer

// root keys:
// HAS_AUTHOR, HAS_CHARACTERS, HAS_BOOK_SAMPLES, HAS_BOOK_SOURCE, HAS_BOOK_STATISTICS, HAS_PROCESSED_ITEMS
export const selectAllBooks = (state) => state.user.storyText && state.user.storyText.books && state.user.storyText.books.books;
export const selectAllBookStatus = (state) => state.user.storyText.status;

export const selectBookContent = (state, bookUuid) => {
  let book = null;
  if (state.user.books && state.user.books.books && bookUuid) {
    book = state.user.books.books.find(book => book.uuid === book);
  }
  else {
    // console.log('not found selectBookContent', bookUuid)
  }
  return book ? book.content : null;
};

export const selectUserAndBookStatistics = (state, bookUuid) => {
  let book = null;
  let bookStats = null;
  if (state && state.user && state.user.books && state.user.books.books && bookUuid) {
    let foundIdx = null;
    book = _.forEach(state.user.books.books, (book, index) => {
      if (book.uuid === book) {
        foundIdx = index;
        return book;
      }
    }
    );
    bookStats = state.user.books.books[foundIdx]
  }
  else {
    // console.log('not found selectBookContent', bookUuid)
  }
  return book ? { book, bookStats } : { book: null, bookStats: {} };
}

// Refactored function
export const selectBookListOptions = (state, disallowedRoles = ENTITY_ROLES.DELETED) => {
  if (state.user.storyText.books?.books) {
    // Filter out books with disallowed roles
    const filteredBooks = state.user.storyText.books.books.filter(x => {
      const bookRole = x.book.role;
      return !disallowedRoles.includes(bookRole);
    });

    let results = filteredBooks.map(x => ({
      key: x.book.uuid,
      text: x.book.name,
      value: x.book.uuid,
      data: x.book
    }));

    // Sort results alphabetically by book.name
    results.sort((a, b) => a.text.localeCompare(b.text));

    return results;
  }
  return [{ key: 'noitems', text: "Book Item", value: 'noitems', data: {} }];
};


export const selectParsedStoryData = (state) => state.user.storyText.parsedStoryData;
export const selectParsedStoryStats = (state) => state.user.storyText.parsedStoryData?.dnObj?.bookStats;

export const selectFetchparseDataFromS3Status = (state) => state.user.storyText.fetchparseDataFromS3Status;
export const selectFetchparseDataFromS3Error = (state) => state.user.storyText.fetchparseDataFromS3Error;

export const selectFullBookTreeData = (state) => state.user.storyText.activeFullBookTreeData;
export const selectFetchFullBookStatus = (state) => state.user.storyText.fetchFullBookStatus;
export const selectFetchFullBookError = (state) => state.user.storyText.fetchFullBookError;

export const selectFullBookDataSet = (state) => state.user.storyText.fullBookDataSet;
export const selectEditedStoryJson = (state) => state.user.storyText.editedStoryJson;
export const selectClearStory = (state) => state.user.storyText.clearStoryProcess;
